import React from "react";
import GameCard from "../../../shared/components/GameCard";
import { SecondaryButton } from "../../../shared/components/buttons";
import SectionHeader from "./SectionHeader";
import GameCardSkeleton from "./GameCardSkeleton";

/**
 * Reusable lobby game row: header + responsive grid of GameCards, with full
 * loading / empty / error coverage. Backs the Slots, Live, Table, Instant,
 * Favorites and Recently Played rows so each gets identical state handling
 * (skeleton, not spinner; empty shows the next action; error shows the real
 * reason + retry) instead of the six divergent inline blocks the lobby had.
 *
 * Module-scope, memoized: a favorite toggle elsewhere on the page no longer
 * forces every row to re-render.
 */
const LobbyGameRow = ({
  title,
  kicker,
  onViewAll,
  games = [],
  isLoading = false,
  isError = false,
  error = null,
  onRetry,
  skeletonCount = 5,
  emptyTitle,
  emptyHint,
  // GameCard pass-through flags
  showFavorite = false,
  showAddedDate = false,
  showNameBelow = false,
  showLastPlayed = false,
  alwaysFavorited = false,
  isFavorited,
  onToggleFavorite,
  onGameLaunch,
  keyPrefix,
  // Per-row grid + slice (slots row is the headline tier: fewer, larger cards)
  gridClassName = "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6",
  limit,
}) => {
  const visibleGames =
    typeof limit === "number" ? games.slice(0, limit) : games;
  const hasEmptyState = Boolean(emptyTitle || emptyHint);

  return (
    <section>
      <SectionHeader title={title} kicker={kicker} onViewAll={onViewAll} />

      <div className={gridClassName}>
        {isLoading ? (
          <GameCardSkeleton count={skeletonCount} />
        ) : isError ? (
          <div className="col-span-full text-center py-8">
            <p className="text-destructive mb-4">
              {error?.message || "We couldn't load these games."}
            </p>
            {onRetry && (
              <SecondaryButton onClick={onRetry} className="cursor-pointer">
                Try Again
              </SecondaryButton>
            )}
          </div>
        ) : visibleGames.length === 0 && hasEmptyState ? (
          <div className="col-span-full text-center py-10">
            {emptyTitle && (
              <p className="text-foreground/90 font-medium mb-1.5">
                {emptyTitle}
              </p>
            )}
            {emptyHint && (
              <p className="text-muted-foreground text-sm">{emptyHint}</p>
            )}
          </div>
        ) : (
          visibleGames.map((game, i) => (
            <div
              key={`${keyPrefix}-${game.game_code}`}
              className="animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both duration-300 ease-out motion-reduce:animate-none"
              style={{ animationDelay: `${Math.min(i, 6) * 45}ms` }}
            >
              <GameCard
                game={
                  game.last_played
                    ? { ...game, lastPlayed: new Date(game.last_played) }
                    : game
                }
                onClick={onGameLaunch}
                showFavorite={showFavorite}
                showAddedDate={showAddedDate}
                showNameBelow={showNameBelow}
                showLastPlayed={showLastPlayed}
                isFavorited={
                  alwaysFavorited
                    ? true
                    : isFavorited
                      ? isFavorited(game.game_code)
                      : false
                }
                onToggleFavorite={onToggleFavorite}
              />
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default React.memo(LobbyGameRow);
